import { Component, ViewChild } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';

import { InputTextModule } from 'primeng/inputtext';
import { TypeOperationAdminDto } from '../model/type-operation-admin.dto';
import { ParametreOperationAdmin } from '../model/parametre-operation-admin';
import { OperationsAdminService } from '../service/operations-admin.service';
import { OperationAdminDto } from '../model/operation-admin-dto';
import { AbstractSigaleComponent } from '../../abstract-sigale.component';
import { ButtonModule } from 'primeng/button';
import { NgIf } from '@angular/common';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ConfirmationModalComponent } from '../../confirmation-modal/confirmation-modal.component';
import { ConfirmationModalService } from '../../confirmation-modal/confirmation-modal.service';
import { ConfirmationData } from '../../confirmation-modal/confirmation-data.model';
import { ConfirmationAction } from '../../confirmation-modal/confirmation-action.model';
import { ApplicationErreur } from '../../../exception/ApplicationErreur';
import { MessageService } from 'primeng/api';
import { ParametreOperationDto } from '../model/parametre-operation.dto';
import { InputSwitchModule } from 'primeng/inputswitch';

@Component({
	selector: 'sigale-gestion-operations-admin-component',
	templateUrl: './gestion-operations-admin.component.html',
	styleUrls: ['./gestion-operations-admin.component.scss'],
	standalone: true,
	imports: [
		TableModule,
		TranslateModule,
		ToastModule,
		DropdownModule,
		ReactiveFormsModule,
		FormsModule,
		InputTextModule,
		ButtonModule,
		NgIf,
		SelectButtonModule,
		InputTextareaModule,
		ConfirmationModalComponent,
		InputSwitchModule,
		ReactiveFormsModule
	]
})
export class GestionOperationsAdminComponent extends AbstractSigaleComponent {
	static readonly TOUS: string = 'TOUS'

	@ViewChild('confirmationModalComponent') confirmationModalComponent: ConfirmationModalComponent;

	typesOperationAdminPossibles: TypeOperationAdminDto[] = [];
	typeOperationAdminSelectionnee: TypeOperationAdminDto;
	loading = true;
	formParParametre: Map<ParametreOperationAdmin, FormGroup>;
	parametresFormArray: FormArray = new FormArray([]);
	inclureAdmin: boolean = false;

	constructor(
		private operationAdminService: OperationsAdminService,
		private confirmationModalService: ConfirmationModalService,
		private messageService: MessageService
	) {
		super();
	}

	ngOnInit() {
		super.ngOnInit();
		this.listerOperationsAdmin();
	}

	/**
	 * Recharge le formgroup à chaque changement de type d'opération d'admin.
	 */
	onChangeTypeOperationAdmin(): void {
		this.parametresFormArray = new FormArray([]);
		this.formParParametre = new Map<ParametreOperationAdmin, FormGroup>();

		if (!this.typeOperationAdminSelectionnee) {
			return;
		}

		this.typeOperationAdminSelectionnee.parametres.forEach(parametre => {
			const parametreFormGroup: FormGroup = new FormGroup({
				valeur: parametre.obligatoire ? new FormControl('', Validators.required) : new FormControl('')
			});
			this.formParParametre.set(parametre, parametreFormGroup);
			this.parametresFormArray.push(parametreFormGroup);
		});
	}

	/**
	 * Liste les opérations d'admin et sélectionne la première opération.
	 */
	listerOperationsAdmin(): void {
		this.operationAdminService.listerTypesOperationAdmin()
			.pipe(finalize(() => this.loading = false))
			.subscribe({
				next: typesOperationAdminPossibles => {
					this.typesOperationAdminPossibles = typesOperationAdminPossibles.sort((a, b) => a.designation.localeCompare(b.designation));
					this.typeOperationAdminSelectionnee = typesOperationAdminPossibles[0];
					this.onChangeTypeOperationAdmin();
				},
				error: error => {
					this.afficherToastError(error.error);
				}
			});
	}

	/**
	 * Quand l'opération est confirmée, envoie la notification d'opération.
	 */
	confirmerOperationAdmin(): void {
		if (!this.typeOperationAdminSelectionnee) {
			return;
		}

		this.loading = true;
		const parametres: ParametreOperationDto[] = [];

		let tous = false;
		this.typeOperationAdminSelectionnee.parametres.forEach(parametre => {
			if (this.formParParametre.get(parametre).get('valeur').value.toUpperCase() === GestionOperationsAdminComponent.TOUS) {
				tous = true;
			}
			else {
				parametres.push(new ParametreOperationDto(parametre.nom, this.formParParametre.get(parametre).get('valeur').value));
			}
		});

		const notificationOperationAdminDto = new OperationAdminDto(this.typeOperationAdminSelectionnee.code, parametres, this.inclureAdmin, tous);
		this.operationAdminService.executerOperation(notificationOperationAdminDto)
			.pipe(finalize(() => this.loading = false))
			.subscribe({
				next: () => {
					this.messageService.add({
						severity: 'success',
						detail: this.translateService.instant('SIGALE_ANGULAR.COMPONENTS.OPERATION_ADMIN.MESSAGE_SUCCES')
					});
				},
				error: error => {
					this.afficherToastError(error.error);
				}
			});
	}

	/**
	 * Demande la confirmation pour exécuter l'opération.
	 */
	executerOperationAdmin() {
		const confirmationActions = [];
		const boutonOui = new ConfirmationAction('SIGALE_ANGULAR.COMPONENTS.OPERATION_ADMIN.CONFIRMATION_OUI');
		const boutonNon = new ConfirmationAction('SIGALE_ANGULAR.COMPONENTS.OPERATION_ADMIN.CONFIRMATION_NON', 'link');

		boutonOui.onClick.subscribe(() => {
			this.confirmationModalService.fermer();
			this.confirmerOperationAdmin();
		});
		boutonNon.onClick.subscribe(() => {
			this.confirmationModalService.fermer();
		});

		confirmationActions.push(boutonOui);
		confirmationActions.push(boutonNon);

		const confirmationData = new ConfirmationData(
			'SIGALE_ANGULAR.COMPONENTS.OPERATION_ADMIN.CONFIRMATION_TITRE',
			'SIGALE_ANGULAR.COMPONENTS.OPERATION_ADMIN.CONFIRMATION_MESSAGE',
			confirmationActions
		);
		this.confirmationModalService.ouvrir(confirmationData);
	}

	/**
	 * Retourne true si l'exécution est possible et qu'il n'y en a aucune en cours.
	 *
	 * @return true si l'exécution est possible et qu'il n'y en a aucune en cours.
	 */
	executionPossible(): boolean {
		return this.typeOperationAdminSelectionnee && !this.loading && this.parametresFormArray.valid;
	}

	/**
	 * Affiche un toast d'erreur avec le message passé en paramètre.
	 *
	 * @param message le message d'erreur.
	 */
	public afficherToastError(message: ApplicationErreur): void {
		this.messageService.add({ severity: 'error', detail: message.messageAvecCode });
	}

	traductionFR(): any {
		return {
			'SIGALE_ANGULAR': {
				'COMPONENTS': {
					'OPERATION_ADMIN': {
						'CONFIRMATION_TITRE': 'Confirmation',
						'CONFIRMATION_MESSAGE': 'Êtes-vous sûr d\'exécuter cette opération ?',
						'CONFIRMATION_OUI': 'Oui',
						'CONFIRMATION_NON': 'Non',
						'TITRE_SECTION': 'Opérations admin',
						'DESCRIPTION': 'Choisissez le type d\'opération, remplissez les paramètres de l\'opération et exécutez la.',
						'INCLURE_ADMIN': 'Inclure les admins',
						'TYPE_OPERATION': 'Type d\'opération',
						'TABLEAU': {
							'HEADER_NOM': 'Nom',
							'HEADER_DESCRIPTION': 'Description',
							'HEADER_VALEURS_POSSIBLES': 'Valeurs possibles',
							'HEADER_VALEUR': 'Valeur',
							'VALEURS_MULTIPLES_POSSIBLES': 'Plusieurs valeurs possibles avec le séparateur ',
							'VALEUR_UNIQUE': 'Valeur unique',
							'AUCUN_PARAMETRE': 'Aucun paramètre'
						},
						'MESSAGE_SUCCES': 'Succès de l\'exécution de l\'opération',
						'FOOTER': {
							'EXECUTER': 'Exécuter'
						},
						'DESCRIPTION_TYPE_OPERATION_GENERIQUE': 'Description',
						'DESCRIPTION_TYPE_OPERATION': {
							'RAFRAICHIR': 'Force le rafraichissement de chrome de tous les utilisateurs. Les admins ne sont pas inclus si la coche \'inclure les admins\' n\'est pas cochée.',
							'DECONNECTER': 'Déconnecte les utilisateurs dont le login est en paramètre. Les admins ne sont pas inclus si la coche \'inclure les admins\' n\'est pas cochée. Si aucun login n\'est renseigné, tous les utilisateurs seront déconnectés.'
							// AJOUT-OPERATION: Ajouter la description ici. La clef est le code de l'opération.
						},
						'TYPE_OPERATION_GENERIQUE': {
							'RAFRAICHIR': 'Rafraichir',
							'DECONNECTER': 'Déconnecter'
							// AJOUT-OPERATION: Ajouter la traduction ici. La clef est la désignation de l'opération.
						},
						'DESCRIPTION_PARAMETRE_OPERATION': {
							'DESCRIPTIONS_LOGINS': 'Les logins impactés par le rafraichissement. Pour impacter tout le monde (admin selon la checkbox), il faut mettre `TOUS`.',
							// AJOUT-OPERATION: Ajouter la traduction des paramètres. La clef est celle indiquée dans le socle.
						}
					}
				}
			}
		};
	}

	traductionIT(): any {
		return {
			'SIGALE_ANGULAR': {
				'COMPONENTS': {
					'OPERATION_ADMIN': {
						'CONFIRMATION_TITRE': 'Conferma',
						'CONFIRMATION_MESSAGE': 'Sei sicuro di voler eseguire questa operazione?',
						'CONFIRMATION_OUI': 'Sì',
						'CONFIRMATION_NON': 'No',
						'TITRE_SECTION': 'Operazioni amministrative',
						'DESCRIPTION': 'Scegli il tipo di operazione, compila i parametri dell\'operazione ed eseguila.',
						'INCLURE_ADMIN': 'Includi amministratori',
						'TYPE_OPERATION': 'Tipo di operazione',
						'TABLEAU': {
							'HEADER_NOM': 'Nome',
							'HEADER_DESCRIPTION': 'Descrizione',
							'HEADER_VALEURS_POSSIBLES': 'Valori possibili',
							'HEADER_VALEUR': 'Valore',
							'VALEURS_MULTIPLES_POSSIBLES': 'Più valori possibili con il separatore ',
							'VALEUR_UNIQUE': 'Valore unico',
							'AUCUN_PARAMETRE': 'Nessun parametro'
						},
						'MESSAGE_SUCCES': 'Operazione eseguita con successo',
						'FOOTER': {
							'EXECUTER': 'Esegui'
						},
						'DESCRIPTION_TYPE_OPERATION_GENERIQUE': 'Descrizione',
						'DESCRIPTION_TYPE_OPERATION': {
							'RAFRAICHIR': 'Forza l\'aggiornamento di Chrome per tutti gli utenti. Gli amministratori non sono inclusi se l\'opzione \'includi amministratori\' non è selezionata.',
							'DECONNECTER': 'Disconnette gli utenti il cui login è fornito come parametro. Gli amministratori non sono inclusi se l\'opzione \'includi amministratori\' non è selezionata. Se nessun login è specificato, tutti gli utenti saranno disconnessi.'
							// AJOUT-OPERATION: Ajouter la description ici. La clef est le code de l'opération.
						},
						'TYPE_OPERATION_GENERIQUE': {
							'RAFRAICHIR': 'Aggiorna',
							'DECONNECTER': 'Disconnetti'
							// AJOUT-OPERATION: Ajouter la traduction ici. La clef est la désignation de l'opération.
						},
						'DESCRIPTION_PARAMETRE_OPERATION': {
							'DESCRIPTIONS_LOGINS': 'I login interessati dall\'aggiornamento. Per influenzare tutti (amministratori inclusi secondo la casella di controllo), è necessario inserire `TOUS`.',
							// AJOUT-OPERATION: Ajouter la traduction des paramètres. La clef est celle indiquée dans le socle.
						}
					}
				}
			}
		};
	}
}
